import { useState } from 'react';
import { FaArrowRight, FaClock, FaTag } from 'react-icons/fa';

const articles = [
  {
    id: 1,
    category: 'Cloud',
    categoryColor: 'bg-cyan-100 text-cyan-700',
    title: 'Navigating the Cloud Migration Journey: Key Lessons for 2024',
    excerpt: 'Cloud adoption continues to accelerate, yet many organizations still struggle with migrations that run over budget or timeline. We share the top five lessons from our most successful Azure migrations this year.',
    date: 'March 28, 2024',
    readTime: '6 min read',
    authorColor: 'bg-violet-600',
    featured: true,
  },
  {
    id: 2,
    category: 'Data & Analytics',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    title: 'From Raw Data to Real Decisions: Building a Modern Data Stack',
    excerpt: 'The modern data stack has evolved rapidly. Learn how combining cloud data warehouses, dbt, and Power BI can give your organization a competitive edge in data-driven decision making.',
    date: 'March 12, 2024',
    readTime: '5 min read',
    authorColor: 'bg-cyan-600',
    featured: false,
  },
  {
    id: 3,
    category: 'Cybersecurity',
    categoryColor: 'bg-red-100 text-red-700',
    title: 'Zero Trust Architecture: Why Traditional Perimeters No Longer Work',
    excerpt: 'With remote work here to stay, the traditional "castle and moat" approach to network security is obsolete. Explore why Zero Trust is becoming the new standard and how to begin your implementation.',
    date: 'February 22, 2024',
    readTime: '7 min read',
    authorColor: 'bg-blue-700',
    featured: false,
  },
  {
    id: 4,
    category: 'Software Development',
    categoryColor: 'bg-violet-100 text-violet-700',
    title: 'Microservices vs. Monolith: Choosing the Right Architecture in 2024',
    excerpt: 'Microservices have dominated enterprise architecture conversations for years. But are they always the right choice? We break down when to decompose and when to stay the course.',
    date: 'February 8, 2024',
    readTime: '8 min read',
    authorColor: 'bg-violet-600',
    featured: false,
  },
  {
    id: 5,
    category: 'AI & Automation',
    categoryColor: 'bg-orange-100 text-orange-700',
    title: 'Practical AI: How IT Teams Are Using Copilot and LLMs Right Now',
    excerpt: 'Generative AI isn\'t just hype — our engineering teams are using it daily to write code faster, document systems, and automate support workflows. Here\'s what\'s actually working.',
    date: 'January 30, 2024',
    readTime: '5 min read',
    authorColor: 'bg-cyan-600',
    featured: false,
  },
  {
    id: 6,
    category: 'IT Consulting',
    categoryColor: 'bg-blue-100 text-blue-700',
    title: 'Building a Vendor Selection Framework for Enterprise IT',
    excerpt: 'Choosing the wrong technology vendor can set an organization back years. We share our proven framework for evaluating, comparing, and selecting enterprise IT vendors with confidence.',
    date: 'January 15, 2024',
    readTime: '6 min read',
    authorColor: 'bg-emerald-700',
    featured: false,
  },
];

const categories = ['All', 'Cloud', 'Data & Analytics', 'Cybersecurity', 'Software Development', 'AI & Automation', 'IT Consulting'];

export default function Insights() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? articles
    : articles.filter(a => a.category === activeCategory);

  const featured = articles.find(a => a.featured);
  const rest = filtered.filter(a => !a.featured || activeCategory !== 'All');

  return (
    <div className="bg-white">

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0c1b4d] to-[#1d4ed8] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="relative max-w-7xl mx-auto px-4">
          <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Knowledge Hub</span>
          <h1 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Insights & Resources</h1>
          <p className="text-blue-200 max-w-2xl text-lg leading-relaxed">
            Expert perspectives on cloud, software, data, security, and the future of enterprise technology from the MARVY team.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" className="w-full fill-white" preserveAspectRatio="none">
            <path d="M0,40 C480,0 960,40 1440,0 L1440,40 Z" />
          </svg>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">

          {/* Featured Article */}
          {activeCategory === 'All' && featured && (
            <div className="mb-14">
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-blue-100 rounded-3xl p-8 md:p-10">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="bg-[#0c1b4d] text-white text-xs font-semibold px-3 py-1 rounded-full">Featured</span>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${featured.categoryColor}`}>{featured.category}</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#0c1b4d] mb-3 leading-tight">{featured.title}</h2>
                    <p className="text-gray-600 leading-relaxed mb-5">{featured.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-5">
                      <span className="flex items-center gap-1.5"><FaClock size={12} /> {featured.readTime}</span>
                      <span>{featured.date}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 ${featured.authorColor} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                        {featured.authorInitials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{featured.author}</p>
                        <p className="text-xs text-gray-400">{featured.authorTitle}</p>
                      </div>
                    </div>
                  </div>
                  <div className="md:w-64 flex-shrink-0 w-full">
                    <div className="bg-gradient-to-br from-[#0c1b4d] to-[#1d4ed8] rounded-2xl aspect-video flex items-center justify-center">
                      <div className="text-center text-white p-6">
                        <div className="text-4xl font-extrabold text-cyan-400">2024</div>
                        <div className="text-sm text-blue-200 mt-1">Cloud Migration Guide</div>
                      </div>
                    </div>
                    <button className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors text-sm">
                      Read Article <FaArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-[#0c1b4d] text-white'
                    : 'bg-slate-100 text-gray-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Article Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {rest.map((article) => (
              <article key={article.id} className="border border-gray-100 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col">
                <div className="bg-gradient-to-br from-[#0c1b4d] to-[#1d4ed8] h-36 flex items-center justify-center">
                  <div className="text-center text-white">
                    <FaTag className="mx-auto text-cyan-400 mb-2" size={20} />
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${article.categoryColor}`}>
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                    <span className="flex items-center gap-1"><FaClock size={11} /> {article.readTime}</span>
                    <span>{article.date}</span>
                  </div>
                  <h3 className="font-bold text-[#0c1b4d] text-base leading-snug mb-3">{article.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1">{article.excerpt}</p>
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 ${article.authorColor} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                        {article.authorInitials}
                      </div>
                      <span className="text-xs text-gray-500">{article.author}</span>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 transition-colors">
                      Read <FaArrowRight size={11} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-14 bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-[#0c1b4d] mb-2">Stay in the Know</h2>
          <p className="text-gray-500 mb-6 text-sm">Subscribe to our monthly newsletter for the latest IT insights, industry news, and MARVY updates.</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button className="px-6 py-3 bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors text-sm whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
